// La navigation commune (D-139, maquettes n1 à n4) : branchée sur le routeur (app.ts), elle pose seule, sur chaque page,
//   * « ‹ X » : X est le nom de la page d'où l'on vient (gardé dans l'historique du navigateur, `history.state`), et le
//     geste y ramène (`history.back()`) ; sans historique (notification push, lien, app rouverte), le parent du module
//     (src/domain/nav.ts, parentOf), sans entrée de plus ;
//   * l'onglet allumé : celui de la page d'origine ;
//   * la ⚙ de la page d'un module, à gauche du « ? », pour qui peut régler ;
//   * les segments, filtres et semaines (`nav.seg a`, `[data-replace]`) remplacent l'adresse au lieu de s'empiler.
// Elle donne aussi aux modules leur page de réglages, faite de rubriques repliables (`settingsPage`), et le renvoi vers
// les réglages d'un autre module (`renvoi`).
import './nav.css';
import './assistant.css';
import { WIZARDS, type WizKey } from '../domain/assistant';
import { clearReview } from '../lib/assistant-api';
import { SETTINGS, parentOf, settingsOfRoute, shortTitle, titleOfHash, type SettingsInfo, type SettingsKey } from '../domain/nav';
import type { Ctx } from './context';
import { $, $$, html, mount, type Html } from './dom';

/** Une page vue : son adresse, son nom, l'onglet allumé. */
export interface NavEntry { hash: string; title: string; tab: string | null }
interface NavState { from: NavEntry | null }

const hashNow = () => location.hash || '#/';
/** La page affichée (la dernière rendue) ; `named` : une page d'onglet, qui garde le nom de son onglet. */
let cur: (NavEntry & { named?: boolean }) | null = null;

const stateOf = (): Record<string, unknown> & { arceau?: NavState } => {
  const s = history.state as unknown;
  return s && typeof s === 'object' ? { ...(s as Record<string, unknown>) } : {};
};

/** Au début du rendu d'une adresse : d'où vient-on ? Une entrée d'historique déjà vue (retour, rechargement) garde sa
 *  provenance ; une nouvelle (un lien touché, `location.hash = …`) vient de la page affichée jusqu'ici. */
export function beginNav(view: HTMLElement | null): NavEntry | null {
  const hash = hashNow();
  if (cur && !cur.named && view?.dataset.navHash === cur.hash) cur.title = titleOf(view, cur.hash, null) ?? cur.title;   // son nom tel qu'il est affiché
  const st = stateOf();
  if (st.arceau) return st.arceau.from;
  const from: NavEntry | null = cur && cur.hash !== hash ? { hash: cur.hash, title: cur.title, tab: cur.tab } : null;
  history.replaceState({ ...st, arceau: { from } satisfies NavState }, '');
  return from;
}

/** Aller à une autre vue de la même page (segment, filtre, semaine), ou au parent sans historique : l'adresse est
 *  remplacée, rien ne s'empile. `from` : la provenance gardée (segments) ou oubliée (parent). */
export function navReplace(hash: string, keepFrom = true): void {
  const st = stateOf();
  const old = location.href;
  history.replaceState({ ...st, arceau: keepFrom ? st.arceau ?? { from: null } : { from: null } }, '', hash);
  window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: old, newURL: location.href }));
}

/** Le nom d'une page : celui qu'elle déclare (`data-nav-title`), sinon son titre ; l'accueil s'appelle « Accueil ». */
function titleOf(view: HTMLElement, hash: string, tabLabel: string | null): string | null {
  const declared = view.querySelector<HTMLElement>('[data-nav-title]')?.dataset.navTitle;
  if (declared) return declared;
  if (tabLabel) return tabLabel;
  const h = view.querySelector('.screen-head h2') ?? view.querySelector('h2');
  const t = h?.textContent?.replace(/\s+/g, ' ').trim();
  return t ? shortTitle(t) : titleOfHash(hash);
}

/** Qui peut régler : le bureau du club (dans sa casquette), ou une page qui le dit (`data-nav-settings` : le responsable
 *  des salles, un signataire des reçus). */
const canSet = (ctx: Ctx, view: HTMLElement): boolean => (!!ctx.club && ctx.adminOf.some(a => a.id === ctx.club!.id) && ctx.hat !== 'benevole')
  || !!view.querySelector('[data-nav-settings]');

export interface NavPage {
  ctx: Ctx;
  route: string[];
  /** Une page d'onglet (ou un de ses segments) : pas de retour ; son nom est celui de l'onglet. */
  tabRoot: boolean;
  tabLabel: string | null;
  tab: string | null;
  from: NavEntry | null;
}

let watching: MutationObserver | null = null;

/** Après le rendu : le retour, la ⚙, le nom de la page ; un écran qui se redessine (une rubrique rechargée, une page en
 *  deux temps) les retrouve. */
export function finishNav(view: HTMLElement, p: NavPage): void {
  const hash = hashNow();
  view.dataset.navHash = hash;
  watching?.disconnect();
  const apply = () => {
    if (hashNow() !== hash) { watching?.disconnect(); return; }
    placeBack(view, p);
    placeGear(view, p);
    placeReview(view, p);
  };
  apply();
  cur = { hash, title: titleOf(view, hash, p.tabRoot ? p.tabLabel : null) ?? 'Retour', tab: p.tab, named: p.tabRoot && !!p.tabLabel };
  watching = new MutationObserver(() => {
    // On ne suit que les retours et les en-têtes ajoutés (pas chaque frappe).
    if (view.querySelector('.back:not([data-nav-back])') || (settingsOfRoute(p.route) && view.querySelector('.screen-head:not(.nv-has-gear)'))) apply();
  });
  watching.observe(view, { childList: true, subtree: true });
}

function placeBack(view: HTMLElement, p: NavPage): void {
  const backs = $$<HTMLAnchorElement>(view, 'a.back');
  if (p.tabRoot) { backs.forEach(b => b.remove()); return; }
  let list = backs;
  if (!list.length) {
    view.firstElementChild?.insertAdjacentHTML('afterbegin', '<a class="back" href="#/">‹ Retour</a>');
    list = $$<HTMLAnchorElement>(view, 'a.back');
  }
  const own = list[0]!;
  const fallback = own.dataset.navBack ? null : { hash: own.getAttribute('href') ?? '#/', label: (own.textContent ?? '').replace(/^‹\s*/, '') };
  const to = p.from ? { hash: p.from.hash, label: p.from.title } : parentOf(p.route, fallback);
  for (const b of list) {
    if (b.dataset.navBack) continue;
    b.textContent = `‹ ${to.label}`;
    b.setAttribute('href', to.hash);
    b.removeAttribute('data-back');
    b.dataset.navBack = p.from ? 'pop' : 'up';
  }
}

function placeGear(view: HTMLElement, p: NavPage): void {
  const key = settingsOfRoute(p.route);
  if (!key || !canSet(p.ctx, view)) return;
  const head = view.querySelector<HTMLElement>('.screen-head');
  if (!head || head.querySelector('.nv-gear')) return;
  head.classList.add('nv-has-gear');
  head.insertAdjacentHTML('beforeend', gearLink(key).s);
}

/** D-158 : en tête de la page de réglages d'un module activé, « Revoir avec l'assistant » (le bureau) ; sur chaque rubrique
 *  laissée à « Je ne sais pas encore », la pastille « à revoir », retirée dès qu'on l'ouvre. */
function placeReview(view: HTMLElement, p: NavPage): void {
  const page = view.querySelector<HTMLElement>('section.nv-settings[data-settings]');
  const k = page?.dataset.settings as WizKey | undefined;
  const c = p.ctx;
  if (!page || !k || !(k in WIZARDS) || k === 'site' || !c.club || !c.adminOf.some(a => a.id === c.club!.id) || c.hat === 'benevole') return;
  const st = c.modules.get(c.club.id);
  if (!st?.modules.some(m => m.module === k && m.enabled)) return;
  const head = page.querySelector('.screen-head');
  if (head && !page.querySelector('.as-revoir-top')) head.insertAdjacentHTML('afterend', `<a class="as-revoir-top" href="#/club/modules/assistant/${k}" data-guide="as-revoir-top"><span class="ti as-i-repeat" aria-hidden="true"></span>Revoir avec l’assistant ›</a>`);
  const w = st.wizards?.find(x => x.module === k && x.state === 'done');
  for (const d of page.querySelectorAll<HTMLDetailsElement>('details.nv-sec[data-rubric]')) {
    const rubric = d.dataset.rubric!;
    const main = d.querySelector('summary .card-main');
    if (!w?.review.includes(rubric) || !main || main.querySelector('.as-todo-chip')) continue;
    main.insertAdjacentHTML('beforeend', '<span class="as-todo-chip" title="Laissé à « Je ne sais pas encore » dans l’assistant">1 réglage à revoir</span>');
    d.addEventListener('toggle', () => {
      if (!d.open || !w.review.includes(rubric)) return;
      w.review = w.review.filter(x => x !== rubric);
      d.querySelector('.as-todo-chip')?.remove();
      void clearReview(c.club!.id, k, rubric).catch(() => undefined);
    });
  }
}

/** La ⚙ d'un module (32 px, à gauche du « ? »). */
export const gearLink = (key: SettingsKey, cls = 'nv-gear', guide = 'nav-gear'): Html => html`
  <a class="${cls}" href="${SETTINGS[key].href}" aria-label="${SETTINGS[key].title}" data-guide="${guide}"><span class="ti ti-gear" aria-hidden="true"></span></a>`;

// --- Les gestes : retour, segments --------------------------------------------------------------------------------
document.addEventListener('click', (e) => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest?.<HTMLAnchorElement>('a[href^="#/"]');
  if (!a || a.target || a.closest('.gd-layer')) return;
  const back = a.dataset.navBack;
  if (back === 'pop') { e.preventDefault(); history.back(); return; }
  if (back === 'up') { e.preventDefault(); navReplace(a.getAttribute('href')!, false); return; }
  // Un lien dans une feuille (« Créneaux › » d'une salle, « Ranger les créneaux › ») : la feuille se ferme, on y va.
  const sheet = a.closest('.sheet');
  sheet?.querySelector<HTMLElement>('.sheet-x')?.click();
  if (a.closest('.seg, [data-replace]')) {
    e.preventDefault();
    const href = a.getAttribute('href')!;
    if (href !== hashNow()) navReplace(href);
  }
});

// --- La page de réglages d'un module : des rubriques repliables (maquettes n1) ---------------------------------------
export interface Rubrique {
  /** La clé de la rubrique (`#/<module>/reglages/<clé>` l'ouvre). */
  key: string;
  /** Le titre (un Html : avec une pastille, « Prévenir d’une absence NOUVEAU », D-148). */
  title: string | Html;
  /** Le résumé, sous le titre (« 6 / 6 équipes »). */
  sum: string | Html;
  /** Une icône (ti-…) ou un numéro (les rubriques numérotées du bénévolat, D-127). */
  icon?: string;
  num?: number;
  chip?: Html | '';
  guide?: string;
  /** Le contenu, composé à la première ouverture ; `again` recompose la page (résumés compris), les rubriques ouvertes
   *  le restant. */
  body: (el: HTMLElement, again: () => void) => void | Promise<void>;
}

export interface SettingsPage {
  /** La page de réglages d'un module, ou celle d'une équipe (`equipe`, sa ⚙ sur la page de l'équipe, D-148). */
  key: SettingsKey | 'equipe';
  /** Ce qui n'est pas dans SETTINGS (la page d'une équipe) : son adresse, son nom, son surtitre, la page d'où l'on vient. */
  info?: SettingsInfo;
  intro: string;
  rubrics: Rubrique[];
  /** La rubrique demandée par l'adresse : ouverte et amenée à l'écran. */
  open?: string | null;
  foot?: Html | '';
  /** data-guide de la liste des rubriques (visites guidées). */
  guide?: string;
  /** Les rubriques ouvertes à la première visite (sans rubrique demandée). */
  openByDefault?: string[];
}

/** Les rubriques ouvertes de chaque page de réglages, gardées le temps de la visite (une rubrique rechargée reste ouverte). */
const opened = new Map<string, Set<string>>();
let lastAsked: string | null = null;
/** D-158, corrigé le 02/11 : une rubrique intégrée à l'assistant d'un module (src/ui/assistant-embeds.ts). Sur cet élément,
 *  la page de réglages ne dessine que le contenu de cette rubrique : ni en-tête, ni « ‹ retour », ni autres rubriques. */
const embedded = new WeakMap<HTMLElement, string>();
export function embedRubric(el: HTMLElement, rubric: string): void { embedded.set(el, rubric); }

/** Monte la page de réglages d'un module : retour vers le module, « Réglages » sous son nom, les rubriques repliables.
 *  `render` la recompose (après un enregistrement). */
export function settingsPage(root: HTMLElement, p: SettingsPage, render: () => Promise<void> | void): void {
  const info = p.info ?? SETTINGS[p.key as SettingsKey];
  const only = embedded.get(root);
  if (only) {
    const r = p.rubrics.find(x => x.key === only);
    mount(root, html`<div class="nv-embed" data-settings-embed="${p.key}" data-rubric="${only}"><div class="nv-body"></div></div>`);
    const body = $(root, '.nv-body')!;
    if (!r) { mount(body, html`<p class="muted small">Rien à régler ici pour l’instant.</p>`); return; }
    void Promise.resolve(r.body(body, () => void render())).catch(() => mount(body, html`<p class="muted small">Chargement impossible pour le moment.</p>`));
    return;
  }
  const asked = p.open && p.rubrics.some(r => r.key === p.open) ? p.open : null;
  // Une nouvelle adresse avec sa rubrique : elle seule s'ouvre ; sinon (recomposition), on garde ce qui était ouvert.
  const askKey = `${p.key}:${hashNow()}`;
  let open = opened.get(p.key);
  if (!open || (asked && lastAsked !== askKey)) open = new Set(asked ? [asked] : p.openByDefault ?? []);
  lastAsked = askKey;
  opened.set(p.key, open);
  mount(root, html`
    <section class="screen nv-settings" data-nav-title="${info.title}" data-settings="${p.key}">
      <a class="back" href="${info.parent.hash}">‹ ${info.parent.label}</a>
      <header class="screen-head"><p class="eyebrow">${info.eyebrow}</p><h2>Réglages</h2><p class="muted">${p.intro}</p></header>
      <div class="nv-secs" ${p.guide ? html`data-guide="${p.guide}"` : ''}>${p.rubrics.map(r => html`
        <details class="card nv-sec" data-rubric="${r.key}" ${r.guide ? html`data-guide="${r.guide}"` : ''} ${open!.has(r.key) ? 'open' : ''}>
          <summary>${r.num ? html`<span class="rg-num" aria-hidden="true">${r.num}</span>` : html`<span class="mi-ico" aria-hidden="true"><span class="ti ${r.icon ?? 'ti-gear'}"></span></span>`}
            <span class="card-main"><strong>${r.title}</strong><span class="muted small">${r.sum}</span>${r.chip ?? ''}</span></summary>
          <div class="nv-body"></div></details>`)}</div>
      ${p.foot ?? ''}
    </section>`);
  const again = () => void render();
  const load = (d: HTMLDetailsElement) => {
    const r = p.rubrics.find(x => x.key === d.dataset.rubric);
    const body = $(d, '.nv-body');
    if (!r || !body || body.dataset.loaded) return;
    body.dataset.loaded = '1';
    mount(body, html`<div class="loading small" aria-busy="true"><div class="ball spin" aria-hidden="true"></div></div>`);
    void Promise.resolve(r.body(body, again)).catch(() => mount(body, html`<p class="muted small">Chargement impossible pour le moment.</p>`));
  };
  for (const d of $$<HTMLDetailsElement>(root, 'details.nv-sec')) {
    if (d.open) load(d);
    d.addEventListener('toggle', () => {
      if (d.open) { open!.add(d.dataset.rubric!); load(d); } else open!.delete(d.dataset.rubric!);
    });
  }
  if (asked) bringIntoView(root, `details.nv-sec[data-rubric="${asked}"]`);
}

/** La rubrique demandée vient à l'écran, sous la barre du haut, une fois son contenu affiché. */
function bringIntoView(root: HTMLElement, sel: string): void {
  let tries = 0;
  const go = () => {
    const el = $<HTMLElement>(root, sel);
    if (!el) return;
    if ($(el, '.nv-body .loading') && ++tries < 30) { window.setTimeout(go, 100); return; }
    window.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + window.scrollY - 130));
  };
  window.setTimeout(go, 60);
}

/** Le renvoi vers les réglages d'un autre module (« Elles se règlent avec les parcours des bénévoles · ⚙ Bénévolat › »). */
export const renvoi = (text: string, label: string, href: string, guide = ''): Html => html`
  <a class="nv-renvoi" href="${href}" ${guide ? html`data-guide="${guide}"` : ''}><span>${text}</span><b><span class="ti ti-gear" aria-hidden="true"></span>${label} ›</b></a>`;

/** Une liste de lignes dans une rubrique (titre, une ou deux lignes, › ou une puce) ; `data` : attributs du bouton. */
export const navRows = (items: { t: string; s?: (string | Html)[]; end?: Html | ''; attrs?: Html | '' }[], guide = ''): Html => html`
  <ul class="nv-rows" ${guide ? html`data-guide="${guide}"` : ''}>${items.map(i => html`
    <li><button type="button" class="nv-row" ${i.attrs ?? ''}><span class="card-main"><strong>${i.t}</strong>${(i.s ?? []).map(s => html`<span class="muted small">${s}</span>`)}</span>
      ${i.end ?? html`<span class="nv-go" aria-hidden="true">›</span>`}</button></li>`)}</ul>`;
